import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Camera, Upload, MapPin, Navigation, CheckCircle, AlertTriangle,
  User, Home, Phone, Image as ImageIcon, FileText, Clock, Building2, Trash2, Eye,
  List, Plus, RefreshCw, Briefcase, GraduationCap, FileCheck, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Base photo types for all check types
const BASE_PHOTO_TYPES = [
  { id: 'fe_selfie', label: 'Field Executive Selfie', required: true, description: 'Your selfie at the location' },
  { id: 'verifier_id_proof', label: 'Verifier ID Proof', required: true, description: 'Your ID card/proof' }
];

// Address specific photos
const ADDRESS_PHOTO_TYPES = [
  { id: 'house_photo_1', label: 'House Photo 1', required: true, description: 'Front view of the residence' },
  { id: 'house_photo_2', label: 'House Photo 2', required: true, description: 'Another angle of the house' },
  { id: 'landmark_photo_1', label: 'Landmark Photo 1', required: true, description: 'Nearby landmark for location reference' },
  { id: 'landmark_photo_2', label: 'Landmark Photo 2', required: true, description: 'Another nearby landmark' }
];

// Employment/Education document photos
const DOCUMENT_PHOTO_TYPES = [
  { id: 'verified_doc_1', label: 'Verified Document 1', required: false, description: 'Photo of verified document' },
  { id: 'verified_doc_2', label: 'Verified Document 2', required: false, description: 'Additional document photo' }
];

const RESIDENCE_STATUS_OPTIONS = [
  'Own House',
  'Rented House',
  'Paying Guest',
  'Company Accommodation',
  'Hostel',
  "Relative's House",
  'Other'
];

// Detect check type category
const getCheckCategory = (checkType) => {
  const type = (checkType || '').toLowerCase();
  if (type.includes('address') || type.includes('residence')) return 'address';
  if (type.includes('employment') || type.includes('experience')) return 'employment';
  if (type.includes('education') || type.includes('degree') || type.includes('qualification')) return 'education';
  if (type.includes('criminal') || type.includes('court')) return 'criminal';
  return 'address'; // default
};

export default function FieldVerificationApp() {
  const { caseId, checkId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [checkData, setCheckData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [checkCategory, setCheckCategory] = useState('address');
  const [insufficiencyBlocked, setInsufficiencyBlocked] = useState(false);
  // View-only lock — set when the check is already submitted / verified so
  // the FE cannot edit the form again but CAN see the details they submitted.
  // (Any corrections post-submission are made by Agent Vendor Team through the
  // internal Verification Update dialog, not on this public form.)
  const [viewOnly, setViewOnly] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);

  // Hardening: when viewOnly flips on, programmatically apply native
  // `disabled` + `readOnly` to every form control inside a locked card so
  // keyboard tab+type is ALSO blocked (CSS pointer-events blocks pointers
  // only).
  useEffect(() => {
    if (!viewOnly) return;
    const apply = () => {
      const nodes = document.querySelectorAll('.card-form-lock input, .card-form-lock textarea, .card-form-lock select, .card-form-lock button:not([data-testid^="preview-photo-"]):not([data-testid="view-only-back-btn"])');
      nodes.forEach((el) => {
        try {
          if ('disabled' in el) el.disabled = true;
          if ('readOnly' in el) el.readOnly = true;
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-disabled', 'true');
        } catch (_e) { /* noop */ }
      });
    };
    apply();
    const mo = new MutationObserver(apply);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [viewOnly]);
  
  // FE Dashboard state
  const [showDashboard, setShowDashboard] = useState(false);
  const [feAssignments, setFeAssignments] = useState([]);
  const [feToken, setFeToken] = useState(localStorage.getItem('fe_token') || '');
  
  // Camera capture
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [captureTarget, setCaptureTarget] = useState(null);
  
  // Verifier form state
  const [verifierForm, setVerifierForm] = useState({
    // Verifier Details
    residence_confirmed: '',  // Residence Confirmed at Address? - syncs with field_remarks
    person_met: '',  // Person Met (was Your Name)
    verifier_name: '',  // Field Executive Name (for validation)
    relationship: '',  // Relationship with Candidate
    person_contact: '',  // Your Contact Number
    verifier_contact: '',  // Alias for person_contact
    
    // Verification Details
    residence_type: '',  // Type of Residence
    building_type: '',  // House/Building Type
    address_type: '',  // Address Type
    period_of_stay_from: '',  // Period of Stay From
    period_of_stay_to: '',  // Period of Stay To
    neighbor_confirmed: '',  // Neighbor Confirmed Candidate's Residence?
    neighbor_name: '',  // Neighbor name if confirmed
    
    // GPS & Location
    gps_coordinates: '',
    
    // Remarks
    overall_remarks: '',
    
    // Ethics declaration
    ethics_declaration: false
  });

  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [photoForm, setPhotoForm] = useState({
    file_url: '',
    description: ''
  });
  
  // Photo preview modal state
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Auto-capitalize text input (capitalize first letter of each word)
  const autoCapitalize = (text) => {
    if (!text) return '';
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Handle text input with auto-capitalization
  const handleTextInput = (field, value) => {
    const capitalizedValue = autoCapitalize(value);
    setVerifierForm(prev => ({ ...prev, [field]: capitalizedValue }));
  };

  // Handle photo preview
  const handleViewPhoto = (photo) => {
    setPreviewPhoto(photo);
    setShowPreviewModal(true);
  };
  
  // Handle photo retake (delete and allow re-upload)
  const handleRetakePhoto = async (annexureId, photoTypeId) => {
    try {
      await axios.delete(`/field-verification/${caseId}/${checkId}/annexures/${annexureId}`);
      setPhotos(prev => prev.filter(p => p.id !== annexureId));
      setShowPreviewModal(false);
      setPreviewPhoto(null);
      toast.success('Photo removed. You can now capture/upload a new one.');
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  // Get photo types based on check category
  const getPhotoTypes = () => {
    const types = [...BASE_PHOTO_TYPES];
    if (checkCategory === 'address' || checkCategory === 'criminal') {
      types.unshift(...ADDRESS_PHOTO_TYPES);
    }
    if (checkCategory === 'employment' || checkCategory === 'education') {
      types.unshift(...ADDRESS_PHOTO_TYPES.slice(0, 2)); // Just 2 location photos
      types.push(...DOCUMENT_PHOTO_TYPES);
    }
    return types;
  };

  // Fetch case data and photos
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch case details (public endpoint for field app)
        const [caseRes, annexuresRes] = await Promise.all([
          axios.get(`/field-verification/${caseId}/${checkId}`),
          axios.get(`/cases/${caseId}/checks/${checkId}/annexures`).catch(() => ({ data: [] }))
        ]);
        setCaseData(caseRes.data.case);
        setCheckData(caseRes.data.check);
        setEmployeeData(caseRes.data.employee);
        setPhotos(annexuresRes.data || []);

        // Prefill + lock the form when the check is already submitted.
        // Locked when status is verified/completed OR verified_details is
        // present. Corrections post-submission are made by Agent Vendor Team
        // through the internal Verification Update dialog — NOT here.
        const _check = caseRes.data.check || {};
        const _saved = _check.verified_details || {};
        const _fv = _check.field_verification || {};
        const _terminalStatus = ['verified', 'completed', 'clear', 'discrepancy', 'failed', 'unable_to_verify'].includes((_check.status || '').toLowerCase());
        const _hasSavedForm = Object.keys(_saved).some(k => _saved[k] !== '' && _saved[k] !== null && _saved[k] !== undefined);
        if (_terminalStatus || _hasSavedForm) {
          setViewOnly(true);
          setSubmittedAt(_check.completed_at || _check.submitted_at || _check.updated_at || null);
          // Prefill the form from verified_details + field_verification so
          // the FE sees exactly what they submitted (Feb 2026 fix: the
          // Residence Confirmed dropdown was reading only from
          // verified_details and rendering blank because that field lives
          // under field_verification on the server model).
          setVerifierForm(prev => ({
            ...prev,
            ...Object.fromEntries(Object.entries(_saved).filter(([_, v]) => v !== null && v !== undefined)),
            residence_confirmed: _saved.residence_confirmed || _fv.residence_confirmed || prev.residence_confirmed,
            overall_remarks: _saved.overall_remarks || _fv.overall_remarks || _fv.remarks || prev.overall_remarks,
            ethics_declaration: !!_saved.ethics_declaration,
          }));
        }
        
        // Block if insufficiency raised
        if (caseRes.data.has_insufficiency) {
          toast.error('Insufficiency raised for this case. Please ask admin to clear insufficiency before updating.');
          setInsufficiencyBlocked(true);
          setLoading(false);
          return;
        }
        
        // Detect check category
        const category = getCheckCategory(caseRes.data.check?.check_type);
        setCheckCategory(category);
        
        // Save FE token for dashboard
        if (caseRes.data.check?.field_token) {
          setFeToken(caseRes.data.check.field_token);
          localStorage.setItem('fe_token', caseRes.data.check.field_token);
        }
      } catch (error) {
        console.error('Failed to load verification data:', error);
        toast.error('Failed to load verification data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    refreshLocation();
  }, [caseId, checkId]);

  // Refresh GPS location
  const refreshLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6),
            accuracy: position.coords.accuracy
          };
          setCurrentLocation(loc);
          setVerifierForm(prev => ({
            ...prev,
            gps_coordinates: `${loc.latitude}, ${loc.longitude}`
          }));
          setLocationError(null);
        },
        (error) => {
          setLocationError('Unable to get GPS location. Please enable location services.');
          console.error('GPS error:', error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setLocationError('Geolocation not supported by browser');
    }
  };

  // Start camera capture - use front camera for selfie, back camera for others
  const startCapture = async (photoTypeId) => {
    try {
      setCaptureTarget(photoTypeId);
      setCapturing(true);
      
      // Use front camera for selfie photos, back camera for everything else
      const useFrontCamera = photoTypeId === 'fe_selfie' || photoTypeId.includes('selfie');
      const facingMode = useFrontCamera ? 'user' : 'environment';
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: facingMode, width: { ideal: 1280 }, height: { ideal: 720 } } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast.error('Unable to access camera');
      setCapturing(false);
    }
  };

  // Reverse geocode to get address from coordinates
  const reverseGeocode = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`
      );
      const data = await response.json();
      if (data && data.address) {
        const addr = data.address;
        return {
          street: addr.road || addr.neighbourhood || addr.suburb || '',
          city: addr.city || addr.town || addr.village || addr.county || '',
          state: addr.state || '',
          pincode: addr.postcode || '',
          country: addr.country || 'India'
        };
      }
    } catch (error) {
      console.error('Reverse geocode failed:', error);
    }
    return null;
  };

  // Format date for watermark
  const formatWatermarkDate = () => {
    const now = new Date();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const day = days[now.getDay()];
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const date = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${date} ${hours}:${minutes}:${seconds} ${day}`;
  };

  // Draw GPS watermark on canvas
  const drawGPSWatermark = async (ctx, canvas) => {
    if (!currentLocation) return;
    
    // Ensure latitude and longitude are numbers
    const latitude = parseFloat(currentLocation.latitude);
    const longitude = parseFloat(currentLocation.longitude);
    
    // Check if valid coordinates
    if (isNaN(latitude) || isNaN(longitude)) {
      console.warn('Invalid GPS coordinates:', currentLocation);
      return;
    }
    
    // Get address from coordinates
    let addressInfo = await reverseGeocode(latitude, longitude);
    
    // Calculate watermark dimensions
    const padding = 15;
    const lineHeight = 28;
    const headerHeight = 40;
    const totalLines = 3; // Date, Address, Lat-Long
    const watermarkHeight = headerHeight + (totalLines * lineHeight) + padding * 2;
    const startY = canvas.height - watermarkHeight;
    
    // Draw semi-transparent background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, startY, canvas.width, watermarkHeight);
    
    let currentY = startY + padding;
    
    // Header: Camera App
    ctx.fillStyle = '#e74c3c';
    ctx.fillRect(padding, currentY, 30, 25);
    ctx.fillStyle = 'white';
    ctx.font = 'bold 12px Arial';
    ctx.textBaseline = 'middle';
    ctx.fillText('JV', padding + 7, currentY + 12);
    
    ctx.fillStyle = '#e74c3c';
    ctx.font = 'bold 26px Arial';
    ctx.fillText('Camera App', padding + 40, currentY + 12);
    currentY += headerHeight;
    
    // Date-Time
    ctx.fillStyle = 'white';
    ctx.font = '20px Arial';
    ctx.fillText(`Date-Time: ${formatWatermarkDate()}`, padding, currentY);
    currentY += lineHeight;
    
    // Address
    if (addressInfo) {
      const addressStr = `Address: ${addressInfo.street}${addressInfo.street ? ', ' : ''}${addressInfo.city}, ${addressInfo.state} ${addressInfo.pincode}, ${addressInfo.country}`;
      ctx.fillText(addressStr, padding, currentY);
    } else {
      ctx.fillText('Address: Fetching location...', padding, currentY);
    }
    currentY += lineHeight;
    
    // Lat-Long
    ctx.fillText(`Lat-Long: (${latitude.toFixed(6)},${longitude.toFixed(6)})`, padding, currentY);
  };

  // Capture photo from camera
  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Add GPS watermark overlay
    await drawGPSWatermark(ctx, canvas);
    
    // Convert to blob and upload
    canvas.toBlob(async (blob) => {
      try {
        const formData = new FormData();
        formData.append('file', blob, `${captureTarget}_${Date.now()}.jpg`);
        formData.append('annexure_type', captureTarget);
        formData.append('description', getPhotoTypes().find(p => p.id === captureTarget)?.description || '');
        if (currentLocation) {
          formData.append('gps_latitude', currentLocation.latitude);
          formData.append('gps_longitude', currentLocation.longitude);
        }
        
        const res = await axios.post(`/cases/${caseId}/checks/${checkId}/annexures/upload`, formData);
        setPhotos(prev => [...prev, res.data]);
        toast.success('Photo captured successfully');
      } catch (error) {
        toast.error('Failed to upload photo');
      }
      
      stopCapture();
    }, 'image/jpeg', 0.85);
  };

  // Stop camera
  const stopCapture = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setCapturing(false);
    setCaptureTarget(null);
  };

  // Handle photo upload via file from device - NO GPS watermark added
  // (FEs may upload pre-tagged photos from GPS Map Camera or similar apps)
  const handleUploadPhoto = async (photoTypeId, file) => {
    if (!file) {
      toast.error('Please select a photo');
      return;
    }
    
    try {
      setUploadingPhoto(photoTypeId);
      
      // Upload original file directly without adding GPS watermark
      // This preserves any existing GPS tags from external apps (GPS Map Camera, etc.)
      const formData = new FormData();
      formData.append('file', file, `${photoTypeId}_${Date.now()}.${file.name.split('.').pop() || 'jpg'}`);
      formData.append('annexure_type', photoTypeId);
      formData.append('description', getPhotoTypes().find(p => p.id === photoTypeId)?.description || '');
      
      // Still record device GPS for database reference (not added to image)
      if (currentLocation) {
        formData.append('gps_latitude', currentLocation.latitude);
        formData.append('gps_longitude', currentLocation.longitude);
      }
      
      const res = await axios.post(`/cases/${caseId}/checks/${checkId}/annexures/upload`, formData);
      
      setPhotos(prev => [...prev, res.data]);
      toast.success('Photo uploaded successfully');
      setUploadingPhoto(null);
      
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
      setUploadingPhoto(null);
    }
  };

  // Delete photo
  const handleDeletePhoto = async (annexureId) => {
    try {
      await axios.delete(`/cases/${caseId}/checks/${checkId}/annexures/${annexureId}`);
      setPhotos(prev => prev.filter(p => p.id !== annexureId));
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  // Submit verification
  const handleSubmitVerification = async () => {
    // Validate ethics declaration
    if (!verifierForm.ethics_declaration) {
      toast.error('Please accept the ethics declaration');
      return;
    }
    
    // Validate required photos
    const requiredPhotos = getPhotoTypes().filter(p => p.required);
    const uploadedTypes = photos.map(p => p.annexure_type);
    const missingPhotos = requiredPhotos.filter(p => !uploadedTypes.includes(p.id));
    
    if (missingPhotos.length > 0) {
      toast.error(`Please upload required photos: ${missingPhotos.map(p => p.label).join(', ')}`);
      return;
    }
    
    // Validate verifier name - use person_met as the verifier name
    const verifierName = verifierForm.verifier_name || verifierForm.person_met;
    if (!verifierName) {
      toast.error('Please enter your name (Person Met / Verifier Name)');
      return;
    }
    
    // Save FE details locally
    localStorage.setItem('fe_name', verifierName);
    localStorage.setItem('fe_phone', verifierForm.person_contact || verifierForm.verifier_contact);
    
    setSubmitting(true);
    try {
      // Use person_met as verifier_name for submission
      const verifierName = verifierForm.verifier_name || verifierForm.person_met;
      await axios.post(`/field-verification/${caseId}/${checkId}/submit`, {
        ...verifierForm,
        verifier_name: verifierName,  // Ensure verifier_name is set
        photos: photos.map(p => ({ 
          id: p.id, 
          type: p.annexure_type,
          url: p.file_url || p.url,
          label: p.annexure_type?.replace(/_/g, ' '),
          gps_coordinates: p.gps_latitude && p.gps_longitude ? `${p.gps_latitude}, ${p.gps_longitude}` : null,
          gps_latitude: p.gps_latitude,
          gps_longitude: p.gps_longitude
        })),
        check_category: checkCategory,
        submitted_at: new Date().toISOString()
      });
      toast.success('Verification submitted successfully!');
      
      // Show dashboard after submission
      setShowDashboard(true);
    } catch (error) {
      toast.error('Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const getPhotoStatus = (photoType) => {
    return photos.find(p => p.annexure_type === photoType);
  };

  // Fetch FE assignments for dashboard
  const fetchFeAssignments = async () => {
    if (!verifierForm.verifier_name) return;
    try {
      const res = await axios.get(`/field-verification/fe-assignments`, {
        params: { fe_name: verifierForm.verifier_name }
      });
      setFeAssignments(res.data || []);
    } catch (error) {
      console.error('Failed to fetch FE assignments:', error);
    }
  };

  useEffect(() => {
    if (showDashboard && verifierForm.verifier_name) {
      fetchFeAssignments();
    }
  }, [showDashboard, verifierForm.verifier_name]);

  // Get check type icon
  const getCheckIcon = () => {
    switch (checkCategory) {
      case 'employment': return <Briefcase className="w-5 h-5 text-blue-600" />;
      case 'education': return <GraduationCap className="w-5 h-5 text-purple-600" />;
      case 'criminal': return <FileCheck className="w-5 h-5 text-red-600" />;
      default: return <Home className="w-5 h-5 text-teal-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Clock className="w-12 h-12 text-teal-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading verification details...</p>
        </div>
      </div>
    );
  }

  // FE Dashboard View
  if (showDashboard) {
    const pendingCount = feAssignments.filter(a => a.status !== 'completed').length;
    const completedCount = feAssignments.filter(a => a.status === 'completed').length;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100">
        <div className="bg-teal-600 text-white p-4 shadow-lg">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <List className="w-8 h-8" />
                <div>
                  <h1 className="text-xl font-bold">My Assignments</h1>
                  <p className="text-teal-100 text-sm">{verifierForm.verifier_name}</p>
                </div>
              </div>
              <Button size="sm" variant="secondary" onClick={() => setShowDashboard(false)}>
                <Plus className="w-4 h-4 mr-1" />New
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-lg mx-auto p-4 space-y-3">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="py-3 text-center">
                <p className="text-2xl font-bold text-blue-700">{feAssignments.length}</p>
                <p className="text-xs text-blue-600">Total</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-3 text-center">
                <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
                <p className="text-xs text-amber-600">Pending</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="py-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{completedCount}</p>
                <p className="text-xs text-emerald-600">Completed</p>
              </CardContent>
            </Card>
          </div>
          
          <Button variant="outline" className="w-full" onClick={fetchFeAssignments}>
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
          
          {feAssignments.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="font-medium">No assignments found</p>
                <p className="text-sm mt-1">Assignments will appear here when assigned by vendor</p>
              </CardContent>
            </Card>
          ) : (
            feAssignments.map((assignment, idx) => (
              <Card 
                key={assignment.check_id || idx}
                className={`cursor-pointer hover:shadow-md transition-shadow ${
                  assignment.status === 'completed' ? 'bg-emerald-50 border-emerald-200' :
                  assignment.status === 'in_progress' ? 'bg-blue-50 border-blue-200' :
                  'bg-white border-gray-200'
                }`}
                onClick={() => {
                  setShowDashboard(false);
                  navigate(`/field-verification/${assignment.case_id}/${assignment.check_id}`);
                }}
              >
                <CardContent className="py-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">{assignment.employee_name}</p>
                    <Badge className={
                      assignment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      assignment.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }>
                      {assignment.status === 'completed' ? 'Completed' : 
                       assignment.status === 'in_progress' ? 'In Progress' : 'Pending'}
                    </Badge>
                  </div>
                  <div className="space-y-1 text-sm text-gray-500">
                    <p className="flex items-center gap-2">
                      <FileCheck className="w-3 h-3" />
                      {(assignment.check_type || 'Address Check').replace(/_/g, ' ')}
                    </p>
                    {assignment.address && (
                      <p className="flex items-center gap-2 truncate">
                        <MapPin className="w-3 h-3" />
                        {assignment.address}
                      </p>
                    )}
                    <p className="flex items-center gap-2 text-xs text-gray-400">
                      Case: {assignment.case_id}
                    </p>
                    {assignment.assigned_at && (
                      <p className="flex items-center gap-2 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100">
      {/* Header */}
      <div className="bg-teal-600 text-white p-4 shadow-lg">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getCheckIcon()}
              <div>
                <h1 className="text-xl font-bold">Field Verification</h1>
                <p className="text-teal-100 text-sm capitalize">{checkCategory} Verification</p>
              </div>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setShowDashboard(true)}>
              <List className="w-4 h-4 mr-1" />My Tasks
            </Button>
          </div>
        </div>
      </div>

      <div className={`max-w-lg mx-auto p-4 space-y-4 ${viewOnly ? 'fv-view-only' : ''}`}>
        {viewOnly && (
          <style>{`
            .fv-view-only .card-form-lock input,
            .fv-view-only .card-form-lock textarea,
            .fv-view-only .card-form-lock select,
            .fv-view-only .card-form-lock button:not([data-testid^="preview-photo-"]):not([data-testid="view-only-back-btn"]) {
              pointer-events: none !important;
              cursor: not-allowed !important;
              opacity: 0.9;
            }
            .fv-view-only .card-form-lock [role="combobox"] { pointer-events: none !important; opacity: 0.9; }
            .fv-view-only .card-form-lock label { color: #475569; }
            .fv-view-only .card-form-lock input,
            .fv-view-only .card-form-lock textarea,
            .fv-view-only .card-form-lock select { background-color: #f1f5f9 !important; color: #475569 !important; }
          `}</style>
        )}
        {/* Insufficiency Block Warning */}
        {insufficiencyBlocked && (
          <Card className="border-red-300 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-red-800">Insufficiency Raised</p>
                  <p className="text-sm text-red-700 mt-1">This case has an open insufficiency. Please ask admin to clear the insufficiency before updating the field form.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* View-Only banner — shown when the field executive has already
            submitted this check. Prevents duplicate submissions and lets
            the FE see the exact data they submitted. */}
        {viewOnly && !insufficiencyBlocked && (
          <Card className="border-emerald-300 bg-emerald-50" data-testid="field-verification-view-only-banner">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-emerald-800">Verification Completed — View Only</p>
                  <p className="text-sm text-emerald-700 mt-1">
                    You've already submitted this verification{submittedAt ? ` on ${new Date(submittedAt).toLocaleString()}` : ''}. The details below are locked. Contact your ops team if any changes are required.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!insufficiencyBlocked && (<>
        {/* GPS Status */}
        <Card className={currentLocation ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Navigation className={`w-5 h-5 ${currentLocation ? 'text-emerald-600' : 'text-amber-600'}`} />
                <div>
                  <p className="text-sm font-medium">
                    {currentLocation ? 'GPS Location Captured' : 'GPS Location Required'}
                  </p>
                  {currentLocation && (
                    <p className="text-xs text-emerald-700">
                      {currentLocation.latitude}, {currentLocation.longitude}
                    </p>
                  )}
                  {locationError && (
                    <p className="text-xs text-amber-700">{locationError}</p>
                  )}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={refreshLocation}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Case Info */}
        {/* Header with Logo, Check Name and Check ID */}
        <div className="bg-indigo-600 text-white p-4 rounded-lg shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h2 className="text-xl font-bold">
                {(() => {
                  const type = checkData?.check_type || checkCategory || '';
                  const subType = checkData?.subcheck_type || employeeData?.address_type || '';
                  
                  // Build complete check type name
                  if (type.includes('address')) {
                    return `Address ${subType || 'Present'}`;
                  } else if (type.includes('employment')) {
                    return subType?.toLowerCase().includes('latest') ? 'Employment Latest' : 
                           subType?.toLowerCase().includes('previous') ? 'Employment Previous' : 
                           `Employment ${subType || 'Latest'}`;
                  } else if (type.includes('education')) {
                    return subType?.toLowerCase().includes('highest') ? 'Education Highest' :
                           subType?.toLowerCase().includes('latest') ? 'Education Latest' :
                           `Education ${subType || 'Highest'}`;
                  } else if (type.includes('criminal')) {
                    return 'Criminal Verification';
                  } else {
                    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Verification Check';
                  }
                })()}
              </h2>
              {/* Joining Company / Client Name */}
              <p className="text-white font-bold text-base mt-2">
                {caseData?.client_name || 'Company Name'}
              </p>
              <p className="text-indigo-100 text-sm mt-1">
                Check ID: {checkId}
              </p>
            </div>
            <img 
              src="https://customer-assets.emergentagent.com/job_030df3d9-19c4-46fd-bbcb-ffc85061ea5f/artifacts/9vsftlxi_logo%205ft%206ft.jpg" 
              alt="JustVerified" 
              className="h-16 w-auto rounded bg-white p-1.5 shadow-md"
            />
          </div>
        </div>

        {/* Case Details - without Check ID */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-600" />
              Case Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div className="text-gray-500">Case ID:</div>
              <div className="font-medium">{caseId}</div>
              <div className="text-gray-500">Employee:</div>
              <div className="font-medium">{employeeData?.name || checkData?.employee_name || '-'}</div>
              <div className="text-gray-500">Father Name:</div>
              <div className="font-medium">{employeeData?.father_name || '-'}</div>
            </div>
            
            {/* Address Details Section */}
            <div className="pt-2 border-t space-y-2">
              <div className="grid grid-cols-2 gap-2">
                {employeeData?.address_type && (
                  <>
                    <div className="text-gray-500">Address Type:</div>
                    <div className="font-medium">{employeeData.address_type}</div>
                  </>
                )}
                <div className="text-gray-500">Address:</div>
                <div className="font-medium">{employeeData?.address || checkData?.address || '-'}</div>
                <div className="text-gray-500">City:</div>
                <div className="font-medium">{employeeData?.city || '-'}</div>
                <div className="text-gray-500">Landmark:</div>
                <div className="font-medium">{employeeData?.landmark || '-'}</div>
                <div className="text-gray-500">State:</div>
                <div className="font-medium">{employeeData?.state || '-'}</div>
                <div className="text-gray-500">Pincode:</div>
                <div className="font-medium">{employeeData?.pincode || '-'}</div>
                <div className="text-gray-500">Contact:</div>
                <div className="font-medium">{employeeData?.phone || '-'}</div>
                <div className="text-gray-500">Alt Contact:</div>
                <div className="font-medium">{employeeData?.alternate_contact || '-'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Special Instructions Box */}
        {checkData?.special_instructions && (
          <Card className="border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Special Instructions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-bold text-gray-800 whitespace-pre-wrap">
                {checkData.special_instructions}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Additional Remarks Box */}
        {(checkData?.remarks || employeeData?.remarks) && (
          <Card className="border-blue-300 bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                <AlertTriangle className="w-5 h-5" />
                Additional Remarks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-bold text-gray-800 whitespace-pre-wrap">
                {checkData?.remarks || employeeData?.remarks}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Photo Capture Section - MOVED BEFORE DECLARATION */}
        <Card className="card-form-lock">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="w-5 h-5 text-teal-600" />
              Photo Capture
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Camera Preview */}
            {capturing && (
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video ref={videoRef} autoPlay playsInline className="w-full" />
                <canvas ref={canvasRef} className="hidden" />
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                  <Button onClick={capturePhoto} className="bg-white text-black hover:bg-gray-100">
                    <Camera className="w-5 h-5 mr-2" />Capture
                  </Button>
                  <Button onClick={stopCapture} variant="destructive">
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {getPhotoTypes().map((photoType) => {
              const uploaded = getPhotoStatus(photoType.id);
              return (
                <div key={photoType.id} className={`p-3 rounded-lg border ${uploaded ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-sm flex items-center gap-2">
                        {uploaded ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <ImageIcon className="w-4 h-4 text-gray-400" />}
                        {photoType.label}
                        {photoType.required && <span className="text-red-500">*</span>}
                      </p>
                      <p className="text-xs text-gray-500">{photoType.description}</p>
                    </div>
                    {uploaded && (
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleViewPhoto({ ...uploaded, photoTypeLabel: photoType.label })}
                          title="Preview photo"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-amber-600" 
                          onClick={() => handleRetakePhoto(uploaded.id, photoType.id)}
                          title="Retake photo"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {!uploaded && !capturing && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="flex-1 bg-teal-600 hover:bg-teal-700"
                          onClick={() => startCapture(photoType.id)}
                        >
                          <Camera className="w-4 h-4 mr-1" />Capture
                        </Button>
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleUploadPhoto(photoType.id, file);
                              e.target.value = ''; // Reset input
                            }}
                          />
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full"
                            type="button"
                            onClick={(e) => e.currentTarget.parentElement.querySelector('input').click()}
                            disabled={uploadingPhoto === photoType.id}
                          >
                            {uploadingPhoto === photoType.id ? (
                              <>Uploading...</>
                            ) : (
                              <><Upload className="w-4 h-4 mr-1" />Upload</>
                            )}
                          </Button>
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {uploaded?.gps_latitude && uploaded?.gps_longitude && (
                    <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                      <Navigation className="w-3 h-3" />
                      GPS: {uploaded.gps_latitude}, {uploaded.gps_longitude}
                    </p>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Verifier Details */}
        <Card className="card-form-lock">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-teal-600" />
              Verifier Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 1. Residence Confirmed at Address? */}
            <div>
              <Label>Residence Confirmed at Address? *</Label>
              <Select 
                value={verifierForm.residence_confirmed}
                onValueChange={(v) => setVerifierForm(prev => ({ ...prev, residence_confirmed: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes - Candidate resides here</SelectItem>
                  <SelectItem value="no">No - Candidate does not reside here</SelectItem>
                  <SelectItem value="shifted">Shifted to new address</SelectItem>
                  <SelectItem value="not_traceable">Not Traceable</SelectItem>
                  <SelectItem value="left_job_not_cooperating">Left Job / Not Co-operating</SelectItem>
                  <SelectItem value="incorrect_number_switchoff">Incorrect Number / Switch Off</SelectItem>
                  <SelectItem value="not_responding_call">Not Responding Call</SelectItem>
                  <SelectItem value="direct_visit_not_located">Direct Visit - Not Located Address</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* 2. Person Met / Verifier Name - Always visible, syncs with Verification Update form */}
            <div>
              <Label>Person Met / Verifier Name</Label>
              <Input 
                value={verifierForm.person_met}
                onChange={(e) => handleTextInput('person_met', e.target.value)}
                placeholder="Name of person met during verification"
                style={{ textTransform: 'capitalize' }}
              />
            </div>
            
            {/* 3. Relationship with Candidate */}
            <div>
              <Label>Relationship with Candidate</Label>
              <Select 
                value={verifierForm.relationship}
                onValueChange={(v) => setVerifierForm(prev => ({ ...prev, relationship: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Self</SelectItem>
                  <SelectItem value="father">Father</SelectItem>
                  <SelectItem value="mother">Mother</SelectItem>
                  <SelectItem value="spouse">Spouse</SelectItem>
                  <SelectItem value="sibling">Sibling</SelectItem>
                  <SelectItem value="relative">Relative</SelectItem>
                  <SelectItem value="neighbor">Neighbor</SelectItem>
                  <SelectItem value="landlord">Landlord</SelectItem>
                  <SelectItem value="security">Security Guard</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* 4. Your Contact Number - Not Mandatory */}
            <div>
              <Label>Your Contact Number</Label>
              <Input 
                value={verifierForm.person_contact}
                onChange={(e) => setVerifierForm(prev => ({ ...prev, person_contact: e.target.value }))}
                placeholder="+91-XXXXXXXXXX"
              />
            </div>
          </CardContent>
        </Card>

        {/* Verification Details - Only for Address/Criminal */}
        {(checkCategory === 'address' || checkCategory === 'criminal') && (
          <Card className="card-form-lock">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Home className="w-5 h-5 text-teal-600" />
                Verification Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 1. Type of Residence */}
              <div>
                <Label>Type of Residence</Label>
                <Select 
                  value={verifierForm.residence_type}
                  onValueChange={(v) => setVerifierForm(prev => ({ ...prev, residence_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owned">Self-Owned</SelectItem>
                    <SelectItem value="rented">Rented</SelectItem>
                    <SelectItem value="family_owned">Family Owned</SelectItem>
                    <SelectItem value="company_provided">Company Provided</SelectItem>
                    <SelectItem value="paying_guest">Paying Guest (PG)</SelectItem>
                    <SelectItem value="hostel">Hostel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* 2. House/Building Type */}
              <div>
                <Label>House/Building Type</Label>
                <Select 
                  value={verifierForm.building_type}
                  onValueChange={(v) => setVerifierForm(prev => ({ ...prev, building_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="independent_house">Independent House</SelectItem>
                    <SelectItem value="apartment">Apartment/Flat</SelectItem>
                    <SelectItem value="villa">Villa/Bungalow</SelectItem>
                    <SelectItem value="row_house">Row House</SelectItem>
                    <SelectItem value="chawl">Chawl/Tenement</SelectItem>
                    <SelectItem value="slum">Slum Area</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* 3. Address Type (Locality Type) */}
              <div>
                <Label>Locality Type</Label>
                <Select 
                  value={verifierForm.address_type}
                  onValueChange={(v) => setVerifierForm(prev => ({ ...prev, address_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="posh">Posh/Upscale</SelectItem>
                    <SelectItem value="middle_class">Middle Class</SelectItem>
                    <SelectItem value="lower_middle">Lower Middle Class</SelectItem>
                    <SelectItem value="industrial">Industrial Area</SelectItem>
                    <SelectItem value="rural">Rural/Village</SelectItem>
                    <SelectItem value="slum">Slum Area</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* 4. Period of Stay - Split to From & To */}
              <div>
                <Label>Period of Stay</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-gray-500">From</span>
                    <Input 
                      type="month"
                      value={verifierForm.period_of_stay_from}
                      onChange={(e) => setVerifierForm(prev => ({ ...prev, period_of_stay_from: e.target.value }))}
                    />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">To</span>
                    <Input 
                      type="month"
                      value={verifierForm.period_of_stay_to}
                      onChange={(e) => setVerifierForm(prev => ({ ...prev, period_of_stay_to: e.target.value }))}
                      placeholder="Present"
                    />
                  </div>
                </div>
              </div>
              
              {/* 5. Neighbor Confirmed Candidate's Residence? */}
              <div>
                <Label>Neighbor Confirmed Candidate's Residence?</Label>
                <Select 
                  value={verifierForm.neighbor_confirmed}
                  onValueChange={(v) => setVerifierForm(prev => ({ ...prev, neighbor_confirmed: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="na">Not Available</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Neighbor name if confirmed */}
              {verifierForm.neighbor_confirmed === 'yes' && (
                <div>
                  <Label>Neighbor's Name</Label>
                  <Input 
                    value={verifierForm.neighbor_name}
                    onChange={(e) => handleTextInput('neighbor_name', e.target.value)}
                    placeholder="Name of neighbor who confirmed"
                    style={{ textTransform: 'capitalize' }}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Overall Remarks */}
        <Card className="card-form-lock">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Remarks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea 
              value={verifierForm.overall_remarks}
              onChange={(e) => {
                // Capitalize first letter of sentences
                const value = e.target.value;
                const capitalizedValue = value.replace(/(^\w|\.\s+\w)/g, (char) => char.toUpperCase());
                setVerifierForm(prev => ({ ...prev, overall_remarks: capitalizedValue }));
              }}
              placeholder="Any additional observations or comments..."
              rows={3}
              style={{ textTransform: 'none' }}
            />
          </CardContent>
        </Card>

        {/* Ethics Declaration */}
        <Card className="border-amber-200 bg-amber-50 card-form-lock">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="ethics"
                checked={verifierForm.ethics_declaration}
                onCheckedChange={(checked) => setVerifierForm(prev => ({ ...prev, ethics_declaration: checked }))}
                className="mt-1"
              />
              <Label htmlFor="ethics" className="text-sm leading-relaxed cursor-pointer">
                I hereby declare that I have conducted this verification in person, following all ethical guidelines. 
                The information and photographs submitted are genuine and captured at the actual location. 
                I have not fabricated or manipulated any data in this report.
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button 
          className="w-full bg-teal-600 hover:bg-teal-700 h-12 text-lg"
          onClick={handleSubmitVerification}
          disabled={submitting || !verifierForm.ethics_declaration || viewOnly}
          data-testid="submit-verification-btn"
        >
          {viewOnly ? (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              Already Submitted
            </>
          ) : submitting ? (
            <>
              <Clock className="w-5 h-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              Submit Verification
            </>
          )}
        </Button>

        {!verifierForm.ethics_declaration && (
          <p className="text-center text-sm text-amber-600">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            Please accept the ethics declaration before submitting
          </p>
        )}

        {/* Photo Preview Modal */}
        {showPreviewModal && previewPhoto && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowPreviewModal(false);
              setPreviewPhoto(null);
            }}
          >
            <div 
              className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-3 border-b flex items-center justify-between bg-gray-50">
                <div>
                  <h3 className="font-semibold text-gray-800">{previewPhoto.photoTypeLabel || 'Photo Preview'}</h3>
                  <p className="text-xs text-gray-500">{previewPhoto.description || ''}</p>
                </div>
                <button 
                  type="button"
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPreviewModal(false);
                    setPreviewPhoto(null);
                  }}
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="p-2 bg-gray-100">
                <img 
                  src={previewPhoto.file_url} 
                  alt={previewPhoto.photoTypeLabel || 'Captured photo'}
                  className="w-full h-auto max-h-[60vh] object-contain rounded"
                />
              </div>
              {previewPhoto.gps_latitude && previewPhoto.gps_longitude && (
                <div className="px-3 py-2 text-xs text-gray-600 bg-gray-50 flex items-center gap-2">
                  <Navigation className="w-3 h-3" />
                  GPS: {previewPhoto.gps_latitude}, {previewPhoto.gps_longitude}
                </div>
              )}
              <div className="p-3 border-t flex justify-between">
                <button 
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPreviewModal(false);
                    setPreviewPhoto(null);
                  }}
                >
                  Close
                </button>
                <Button 
                  type="button"
                  variant="destructive"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRetakePhoto(previewPhoto.id, previewPhoto.annexure_type);
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retake Photo
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 py-4">
          <p>JustVerified Field Verification App</p>
          <p>Powered by Background Verification CRM</p>
        </div>
        </>)}
      </div>
    </div>
  );
}
